import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, DollarSign } from 'lucide-react';
import apiClient from '../services/api';

const NAVY       = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';

const Currencies = () => {
  const getSessionData = () => {
    try {
      const s = localStorage.getItem('ltms_session');
      if (!s) return null;
      const d = JSON.parse(s);
      return d?.session || d;
    } catch { return null; }
  };

  const session   = getSessionData();
  const companyId = session?.companyid;
  const yearId    = session?.yearid;

  const [currencies,         setCurrencies]         = useState([]);
  const [countries,          setCountries]          = useState([]);
  const [filteredCurrencies, setFilteredCurrencies] = useState([]);
  const [loading,            setLoading]            = useState(true);
  const [searchTerm,         setSearchTerm]         = useState('');
  const [showModal,          setShowModal]          = useState(false);
  const [editMode,           setEditMode]           = useState(false);
  const [submitLoading,      setSubmitLoading]      = useState(false);
  const [errors,             setErrors]             = useState({});

  const [formData, setFormData] = useState({
    currencyid: null, currencyname: '', symbol: '', monetaryunit: '',
    noofdecimals: 2, countryid: '', statusbasecurrency: false, isactive: true,
  });

  useEffect(() => { fetchCountries(); fetchCurrencies(); }, []);
  useEffect(() => { filterCurrencies(); }, [searchTerm, currencies]);

  const fetchCountries = async () => {
    try {
      const res = await apiClient.get('/Countries.php', { params: { active: true } });
      if (res.data.success) setCountries(res.data.data || []);
    } catch { /* silent */ }
  };

  const fetchCurrencies = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/Currencies.php');
      if (res.data.success) setCurrencies(res.data.data || []);
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
    } finally { setLoading(false); }
  };

  const filterCurrencies = () => {
    if (!searchTerm.trim()) { setFilteredCurrencies(currencies); return; }
    const s = searchTerm.toLowerCase();
    setFilteredCurrencies(currencies.filter(c =>
      c.currencyname.toLowerCase().includes(s) ||
      c.symbol?.toLowerCase().includes(s) ||
      c.monetaryunit?.toLowerCase().includes(s) ||
      c.countryname?.toLowerCase().includes(s)
    ));
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.currencyname.trim()) errs.currencyname = 'Currency name is required';
    else if (formData.currencyname.length > 50) errs.currencyname = 'Currency name must be 50 characters or less';
    if (formData.symbol && formData.symbol.length > 10) errs.symbol = 'Symbol must be 10 characters or less';
    if (formData.monetaryunit && formData.monetaryunit.length > 50) errs.monetaryunit = 'Monetary unit must be 50 characters or less';
    if (formData.noofdecimals < 0 || formData.noofdecimals > 10) errs.noofdecimals = 'Decimals must be between 0 and 10';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitLoading(true);
    try {
      const method = editMode ? 'put' : 'post';
      const res = await apiClient[method]('/Currencies.php', {
        ...formData,
        countryid: formData.countryid !== '' ? parseInt(formData.countryid) : null,
        statusbasecurrency: formData.statusbasecurrency ? 1 : 0,
        isactive: formData.isactive ? 1 : 0,
      });
      if (res.data.success) {
        alert(editMode ? 'Currency updated successfully!' : 'Currency created successfully!');
        closeModal(); fetchCurrencies();
      } else { alert('Error: ' + res.data.message); }
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else if (err.response?.status === 409) alert('Currency name already exists. Please use a different name.');
      else alert('Error saving currency. Please try again.');
    } finally { setSubmitLoading(false); }
  };

  const handleEdit = (currency) => {
    setFormData({
      currencyid: currency.currencyid, currencyname: currency.currencyname,
      symbol: currency.symbol || '', monetaryunit: currency.monetaryunit || '',
      noofdecimals: currency.noofdecimals, countryid: currency.countryid || '',
      statusbasecurrency: currency.statusbasecurrency === 1, isactive: currency.isactive === 1,
    });
    setEditMode(true); setShowModal(true); setErrors({});
  };

  const handleDelete = async (id, isBase) => {
    if (isBase) { alert('Cannot delete the base currency. Please set another currency as base first.'); return; }
    if (!window.confirm('Are you sure you want to delete this currency?')) return;
    try {
      const res = await apiClient.delete('/Currencies.php', { data: { currencyid: id } });
      if (res.data.success) { alert('Currency deleted successfully!'); fetchCurrencies(); }
      else alert('Error: ' + res.data.message);
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else if (err.response?.status === 409) alert('Cannot delete the base currency.');
      else alert('Error deleting currency. Please try again.');
    }
  };

  const resetForm    = () => { setFormData({ currencyid: null, currencyname: '', symbol: '', monetaryunit: '', noofdecimals: 2, countryid: '', statusbasecurrency: false, isactive: true }); setEditMode(false); setErrors({}); };
  const openAddModal = () => { resetForm(); setShowModal(true); };
  const closeModal   = () => { setShowModal(false); resetForm(); };

  const inputCls  = (err) => `w-full px-3.5 py-3 border-2 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] ${err ? 'border-red-500 bg-red-50' : 'border-gray-200'}`;
  const selectCls = `w-full px-3.5 py-3 border-2 border-gray-200 rounded-lg text-sm text-gray-800 font-medium bg-white outline-none transition-all duration-200 focus:border-[#0d1c6b] focus:shadow-[0_0_0_4px_rgba(13,28,107,0.1)]`;

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">

      {/* ── HEADER ── */}
      <div
        className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <DollarSign size={22} color="#fff" />
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight" style={{ color: '#fff' }}>Currencies Master</h1>
            <p className="text-[13px]" style={{ color: 'rgba(255,255,255,0.8)' }}>Manage currency information and exchange rates</p>
          </div>
        </div>
        <button
          onClick={openAddModal}
          className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border-0
            text-sm font-bold cursor-pointer transition-all duration-150 hover:bg-blue-50"
          style={{ color: NAVY }}
        >
          <Plus size={17} />
          Add Currency
        </button>
      </div>

      {/* ── TOOLBAR ── */}
      <div className="flex items-center gap-3 flex-wrap px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex-1 min-w-[220px] flex items-center gap-2 bg-gray-50 border-2 border-gray-200 rounded-lg px-3
          focus-within:border-[#0d1c6b] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(13,28,107,0.1)]
          transition-all duration-200">
          <Search size={16} className="text-gray-400 flex-shrink-0" />
          <input
            type="text"
            placeholder="Search by currency name, symbol, unit, or country..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 border-0 outline-none py-2.5 text-sm text-gray-800 bg-transparent font-medium
              placeholder:text-gray-400 placeholder:font-normal"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}
              className="p-1 rounded cursor-pointer bg-transparent border-0 text-gray-400 hover:text-red-500 hover:bg-gray-100">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="text-sm font-semibold text-gray-500 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg whitespace-nowrap">
          Total: {filteredCurrencies.length} {filteredCurrencies.length === 1 ? 'currency' : 'currencies'}
        </div>
      </div>

      {/* ── BODY ── */}
      <div className="flex-1 p-5">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-lg font-semibold">
            Loading currencies...
          </div>
        ) : filteredCurrencies.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-200">
            <DollarSign size={40} className="text-gray-300 mb-4" />
            <p className="text-xl text-gray-400 font-semibold mb-3">No currencies found</p>
            {searchTerm && (
              <button onClick={() => setSearchTerm('')}
                className="text-sm text-blue-600 underline cursor-pointer bg-transparent border-0">
                Clear search
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm">
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                  {['Currency Name', 'Symbol', 'Monetary Unit', 'Decimals', 'Country', 'Base', 'Status', 'Created At', 'Actions'].map(h => (
                    <th key={h}
                      className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap sticky top-0"
                      style={{ color: '#fff', background: 'inherit' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredCurrencies.map(currency => (
                  <tr key={currency.currencyid}
                    className="border-b border-gray-100 transition-all duration-150
                      hover:bg-gray-50 hover:translate-x-0.5 hover:shadow-[-2px_0_0_#0d1c6b]">
                    <td className="px-4 py-3.5 font-semibold text-gray-800">{currency.currencyname}</td>
                    <td className="px-4 py-3.5 font-mono text-gray-600">{currency.symbol || '-'}</td>
                    <td className="px-4 py-3.5 text-gray-600">{currency.monetaryunit || '-'}</td>
                    <td className="px-4 py-3.5 text-center text-gray-600">{currency.noofdecimals}</td>
                    <td className="px-4 py-3.5">
                      {currency.countryname ? (
                        <span className="inline-block px-2.5 py-1 bg-blue-50 text-blue-700 border-2 border-blue-300
                          rounded-lg text-[12px] font-bold uppercase tracking-wide">
                          {currency.countrycode} — {currency.countryname}
                        </span>
                      ) : <span className="text-gray-400">-</span>}
                    </td>
                    <td className="px-4 py-3.5 text-center">
                      {currency.statusbasecurrency === 1 ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold
                          bg-emerald-50 text-emerald-700 border-2 border-emerald-400 uppercase tracking-wide">
                          BASE
                        </span>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide border-2 ${
                        currency.isactive
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-400'
                          : 'bg-red-50 text-red-700 border-red-400'
                      }`}>
                        {currency.isactive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-500">
                      {new Date(currency.createdat).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex gap-1.5">
                        <button onClick={() => handleEdit(currency)} title="Edit"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer
                            bg-violet-100 border-violet-400 text-violet-700 transition-all duration-150
                            hover:bg-violet-700 hover:text-white hover:scale-110 hover:-translate-y-0.5
                            hover:shadow-[0_4px_12px_rgba(109,40,217,0.3)]">
                          <Edit2 size={13} />
                        </button>
                        <button onClick={() => handleDelete(currency.currencyid, currency.statusbasecurrency === 1)} title="Delete"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer
                            bg-red-100 border-red-400 text-red-600 transition-all duration-150
                            hover:bg-red-600 hover:text-white hover:scale-110 hover:-translate-y-0.5
                            hover:shadow-[0_4px_12px_rgba(220,38,38,0.3)]">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          style={{ animation: 'fadeIn 0.2s ease' }}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-[580px] shadow-2xl border border-gray-200 overflow-hidden max-h-[90vh] overflow-y-auto"
            style={{ animation: 'slideUp 0.35s cubic-bezier(0.16,1,0.3,1)' }}
            onClick={e => e.stopPropagation()}
          >
            <div
              className="flex items-center justify-between px-7 py-5"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}
            >
              <h3 className="flex items-center gap-2 text-[18px] font-bold uppercase tracking-wide m-0"
                style={{ color: '#fff' }}>
                <DollarSign size={18} />
                {editMode ? 'Edit Currency' : 'Add New Currency'}
              </h3>
              <button onClick={closeModal}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0
                  bg-white/20 text-white transition-all duration-200 hover:bg-white/30 hover:rotate-90"
                style={{ color: '#fff' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-7">

              {/* Name + Symbol */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">
                    Currency Name <span className="text-red-500">*</span>
                  </label>
                  <input type="text" value={formData.currencyname}
                    onChange={e => setFormData({ ...formData, currencyname: e.target.value })}
                    maxLength="50" placeholder="e.g., US Dollar, Euro"
                    className={inputCls(errors.currencyname)} />
                  {errors.currencyname && <p className="text-xs text-red-600 font-semibold mt-1">⚠ {errors.currencyname}</p>}
                </div>
                <div>
                  <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">Symbol</label>
                  <input type="text" value={formData.symbol}
                    onChange={e => setFormData({ ...formData, symbol: e.target.value })}
                    maxLength="10" placeholder="e.g., ₹, $, €, £"
                    className={inputCls(errors.symbol)} />
                  {errors.symbol && <p className="text-xs text-red-600 font-semibold mt-1">⚠ {errors.symbol}</p>}
                </div>
              </div>

              {/* Monetary Unit + Decimals */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">Monetary Unit</label>
                  <input type="text" value={formData.monetaryunit}
                    onChange={e => setFormData({ ...formData, monetaryunit: e.target.value })}
                    maxLength="50" placeholder="e.g., Dollar, Euro, Pound"
                    className={inputCls(errors.monetaryunit)} />
                  {errors.monetaryunit && <p className="text-xs text-red-600 font-semibold mt-1">⚠ {errors.monetaryunit}</p>}
                </div>
                <div>
                  <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">No. of Decimals</label>
                  <input type="number" value={formData.noofdecimals} min="0" max="10"
                    onChange={e => setFormData({ ...formData, noofdecimals: parseInt(e.target.value) || 0 })}
                    className={inputCls(errors.noofdecimals) + ' text-right'} />
                  {errors.noofdecimals && <p className="text-xs text-red-600 font-semibold mt-1">⚠ {errors.noofdecimals}</p>}
                </div>
              </div>

              {/* Country */}
              <div className="mb-4">
                <label className="block mb-2 text-xs font-bold text-gray-600 uppercase tracking-wide">Country</label>
                <select value={formData.countryid}
                  onChange={e => setFormData({ ...formData, countryid: e.target.value })}
                  className={selectCls}>
                  <option value="">-- Select Country (Optional) --</option>
                  {countries.map(c => (
                    <option key={c.countryid} value={c.countryid}>{c.countryname} ({c.countrycode})</option>
                  ))}
                </select>
              </div>

              {/* Checkboxes */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="flex items-center gap-3 cursor-pointer px-4 py-3 bg-gray-50
                    border-2 border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-all duration-150">
                    <input type="checkbox" checked={formData.statusbasecurrency}
                      onChange={e => setFormData({ ...formData, statusbasecurrency: e.target.checked })}
                      className="w-5 h-5 cursor-pointer accent-[#0d1c6b]" />
                    <span className="text-sm font-semibold text-gray-700">Set as Base Currency</span>
                  </label>
                  <p className="text-[11px] text-gray-400 mt-1.5 px-1">Only one currency can be base at a time</p>
                </div>
                <div>
                  <label className="flex items-center gap-3 cursor-pointer px-4 py-3 bg-gray-50
                    border-2 border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 transition-all duration-150">
                    <input type="checkbox" checked={formData.isactive}
                      onChange={e => setFormData({ ...formData, isactive: e.target.checked })}
                      className="w-5 h-5 cursor-pointer accent-[#0d1c6b]" />
                    <span className="text-sm font-semibold text-gray-700">Active</span>
                  </label>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-5 border-t border-gray-200">
                <button type="button" onClick={closeModal} disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg
                    cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:border-gray-300
                    disabled:opacity-60 disabled:cursor-not-allowed">
                  Cancel
                </button>
                <button type="submit" disabled={submitLoading}
                  className="px-5 py-2.5 text-sm font-bold border-0 rounded-lg cursor-pointer
                    transition-all duration-200 hover:-translate-y-0.5
                    hover:shadow-[0_6px_16px_rgba(13,28,107,0.35)]
                    disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                  style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { transform:translateY(40px) scale(0.96);opacity:0 } to { transform:translateY(0) scale(1);opacity:1 } }
      `}</style>
    </div>
  );
};

export default Currencies;